// src/pages/PropertyTypes.jsx
import React, { useMemo, useState } from "react";
import useGuidesList from "../hooks/useGuidesList";
import Container from "../components/Container";
import GuideDownload from "../components/GuideDownload";
// import DownloadsBadge from "../components/DownloadsBadge";

const clean = (s) => (s || "").toLowerCase().trim();
const isType = (a) => {
  const c = clean(a.category);
  return c === "property types" || c === "property type";
};
const isValidDate = (d) => {
  if (!d) return false;
  const dt = new Date(d);
  return !isNaN(dt) && dt.getFullYear() > 2000;
};
const getTags = (a) =>
  (a.tags || a.Tags || "")
    .split(",")
    .map((t) => clean(t))
    .filter(Boolean);

export default function PropertyTypes() {
  const all = useGuidesList();        // same list as other pages
  const items = all.filter(isType);   // only “Property Types”

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("Newest");

  const filtered = useMemo(() => {
    const q = clean(query);
    let arr = items.filter((a) =>
      !q || clean(a.title).includes(q) || clean(a.excerpt).includes(q)
    );
    return arr.sort((a, b) =>
      sort === "Newest"
        ? new Date(b.date) - new Date(a.date)
        : new Date(a.date) - new Date(b.date)
    );
  }, [items, query, sort]);

  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <img
          src="/content/images/guide-hero.jpg"
          alt="Property types header"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-white/30 to-white" />
        <Container className="relative py-16 sm:py-20">
          <h1 className="text-4xl font-bold text-[#003366]">Property Strategy Guides</h1>
          <p className="mt-3 text-[#003366] max-w-2xl">
            Deep-dives on different asset classes and strategies.
          </p>
        </Container>
      </section>

      {/* Controls */}
      <section>
        <Container className="py-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search property strategies…"
              className="w-full sm:w-80 rounded-xl border border-slate-300 px-3 py-2"
            />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-2"
            >
              <option>Newest</option>
              <option>Oldest</option>
            </select>
          </div>
        </Container>
      </section>

      {/* Results */}
      <section>
        <Container className="pb-16">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-[#003366]">
              No matching property type guides yet.
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a) => (
                <article
                  key={a.id || a.slug}
                  className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 overflow-hidden"
                >
                  {/* Image with overlay badge */}
                  <div className="relative">
                    <img src={a.image} alt="" className="h-48 w-full object-cover" />
                  
                  </div>

                  <div className="p-6">
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#003366]">
                      {a.category}
                    </div>
                    <h3 className="mt-1 text-lg font-semibold text-[#003366]">
                      {a.title}
                    </h3>
                    {a.excerpt && (
                      <p className="mt-2 text-[#003366]">{a.excerpt}</p>
                    )}

                    <div className="mt-4 flex items-center justify-between gap-3 text-sm text-[#003366]">
                      

                      <div className="flex items-center gap-3">
                        {/* Same linking as Guides */}
                        <a
                          href={`#/guide/${a.slug}`}
                          className="font-semibold text-[#003366]"
                        >
                          Read more →
                        </a>

                        <GuideDownload
                          guideId={a.id || a.slug || a.guid}
                          guideTitle={a.title || a.name || a.heading || a.slug}
                          slug={a.slug}
                          item={a}
                          label="Download PDF"
                        />
                      </div>
                    </div>

                    {/* Optional tags if present */}
                    {getTags(a).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {getTags(a).map((t) => (
                          <span
                            key={t}
                            className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-[#003366]"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </Container>
      </section>
    </main>
  );
}
